/**
 * Recovery for stale lazy chunks after a deploy.
 *
 * Since #840 the service worker calls `skipWaiting()` + `clients.claim()`, so a new
 * deploy takes control of already-open tabs and `cleanupOutdatedCaches()` drops the
 * previous precache. A tab left open across a deploy keeps running the old bundle, so
 * navigating to one of the lazy routes whose chunk was not loaded yet requests a content
 * hash that no longer exists. Vite raises `vite:preloadError` for precisely this case.
 *
 * Reloading picks up the new bundle. The cooldown keeps a chunk that is genuinely gone
 * (not merely stale) from looping: a second failure inside the window is left to
 * propagate so the error surfaces instead of reloading forever.
 */

const RELOAD_KEY = 'brainhub:preload-reload-at'
export const RELOAD_COOLDOWN_MS = 10_000

/**
 * Decides whether a preload failure should trigger a reload. Kept separate from the
 * wiring so the cooldown is testable without touching `window.location`.
 */
export const handlePreloadError = (
  event: Pick<Event, 'preventDefault'>,
  now: number,
  storage: Storage,
  reload: () => void,
): void => {
  // Distinguir "nunca recargamos" (sin valor) de un timestamp real: un valor ausente o
  // ilegible tiene que recuperar, no bloquear.
  const raw = storage.getItem(RELOAD_KEY)
  const last = raw === null ? null : Number(raw)
  if (last !== null && Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS) return

  // Swallow the error only when we are actually going to recover from it.
  event.preventDefault()
  storage.setItem(RELOAD_KEY, String(now))
  reload()
}

export const installPreloadErrorRecovery = (): void => {
  window.addEventListener('vite:preloadError', (event) => {
    handlePreloadError(event, Date.now(), sessionStorage, () => window.location.reload())
  })
}
